/* assets/bcs-product-lookup.js
 *
 * Shared product-lookup helpers used by:
 *   - sections/valve-schedule.liquid  (BOM importer)
 *   - sections/quick-order.liquid     (quick order section)
 *
 * Public API on window.BCSProductLookup. See spec doc 2026-04-30-quick-order-section-design.md
 * for full API documentation.
 *
 * Critical invariants — do not break (see handoff doc 2026-04-27 Section 9):
 *   1. Promise caching by handle (cache the PROMISE, not the resolved value).
 *   2. published_at filter on Algolia hits — drafts/archived must not surface.
 *   3. Algolia v3/v4 client compat shim retained.
 *   4. URLs /products/{handle}?view=bom-meta and ?view=bom-alts unchanged.
 *   5. Caller is responsible for Promise.allSettled vs Promise.all.
 */
(function() {
  'use strict';

  if (window.BCSProductLookup) return;

  // ---------- Internal state ----------

  var _config = {
    algoliaIndex: null,
    customerTags: []
  };

  var _metaCache = {};
  var _altsCache = {};

  // ---------- Configuration ----------

  function configure(opts) {
    if (!opts) return;
    if (opts.algoliaIndex) _config.algoliaIndex = String(opts.algoliaIndex);
    if (opts.customerTags != null) {
      var tags = opts.customerTags;
      if (typeof tags === 'string') {
        tags = tags.split(',').map(function(s) { return s.trim(); }).filter(Boolean);
      }
      if (!Array.isArray(tags)) tags = [];
      _config.customerTags = tags;
    }
  }

  // ---------- Pure helpers ----------

  function escape(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function normalizeSku(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // True if the product is restricted AND the current customer cannot purchase it.
  // False when there is no restriction (no value, empty, or the literal "NULL").
  function isRestricted(restrictedClassId) {
    if (!restrictedClassId) return false;
    var rc = String(restrictedClassId).trim();
    if (!rc || rc === 'NULL' || rc.toLowerCase() === 'null') return false;
    if (!_config.customerTags.length) return true;
    var rcLower = rc.toLowerCase();
    for (var i = 0; i < _config.customerTags.length; i++) {
      if (String(_config.customerTags[i]).trim().toLowerCase() === rcLower) return false;
    }
    return true;
  }

  // BCS catalog uses vendor-prefixed internal SKUs; manufacturer part numbers live in the
  // supplier-part-number metafield. Score against SPN first, then internal SKU, then title.
  function scoreHit(inputSku, hit) {
    var inLow = String(inputSku || '').toLowerCase();
    var inNorm = normalizeSku(inputSku);
    var spn = (hit.meta && hit.meta.custom && hit.meta.custom['supplier-part-number']) || '';
    var spnLow = String(spn).toLowerCase();
    var spnNorm = normalizeSku(spn);
    var hitSku = String(hit.sku || '').toLowerCase();
    var hitSkuNorm = normalizeSku(hitSku);
    var hitTitle = String(hit.title || '').toLowerCase();

    if (spnLow === inLow && spnLow.length > 0) return { score: 100, reason: 'Exact supplier part number match' };
    if (spnNorm === inNorm && inNorm.length > 0) return { score: 95, reason: 'Normalized supplier part number match' };
    if (hitSku === inLow) return { score: 95, reason: 'Exact SKU match' };
    if (hitSkuNorm === inNorm && inNorm.length > 0) return { score: 90, reason: 'Normalized SKU match' };
    if (hitTitle === inLow) return { score: 95, reason: 'Exact title match' };
    if (spnNorm && inNorm && (spnNorm.indexOf(inNorm) >= 0 || inNorm.indexOf(spnNorm) >= 0)) {
      return { score: 82, reason: 'Partial supplier part number match' };
    }
    if (hitSkuNorm && inNorm && (hitSkuNorm.indexOf(inNorm) >= 0 || inNorm.indexOf(hitSkuNorm) >= 0)) {
      return { score: 80, reason: 'Partial SKU match' };
    }
    if (hitTitle.indexOf(inLow) >= 0 && inLow.length > 2) return { score: 70, reason: 'Title contains input' };
    return { score: 50, reason: 'Fuzzy match' };
  }

  function scoreToStatus(score) {
    if (score >= 95) return 'exact';
    if (score >= 80) return 'partial';
    if (score >= 60) return 'weak';
    return 'unmatched';
  }

  // Inline span variant — used inside candidate-card meta lines.
  function stockSpan(qty, tracked) {
    if (typeof qty === 'number' && isFinite(qty)) {
      if (qty > 0) return '<span style="color:var(--vs-green-text,#2e7d32)">' + qty + ' in stock</span>';
      return '<span style="color:var(--vs-green-text,#2e7d32)">Out of stock — Available to order</span>';
    }
    if (tracked === false) return '<span style="color:var(--vs-green-text,#2e7d32)">In stock</span>';
    return '';
  }

  // Block element — used in matched-row product cell.
  function stockLine(r) {
    if (typeof r.inventoryQuantity === 'number' && isFinite(r.inventoryQuantity)) {
      if (r.inventoryQuantity > 0) return '<div class="vs-stock">' + r.inventoryQuantity + ' in stock</div>';
      return '<div class="vs-stock">Out of stock — Available to order</div>';
    }
    if (r.inventoryTracked === false && r.available !== false) return '<div class="vs-stock">In stock</div>';
    return '';
  }

  // ---------- Algolia ----------

  function _client() {
    return window.algoliaShopify && window.algoliaShopify.searchClient;
  }

  // Multi-index search across Algolia search-client versions (the client is provided by the Algolia
  // Shopify app via window.algoliaShopify.searchClient). Capability-detected so v4 behavior is unchanged:
  //   v3: multipleQueries(queries)                          — array + nested `params`
  //   v4: search(queries)                                   — array + nested `params` (has initIndex)
  //   v5: search({ requests: [...] })                       — object wrapper + FLATTENED params (no initIndex)
  // All three resolve to { results: [...] }, so callers are untouched.
  function algoliaMultiSearch(queries) {
    var c = _client();
    if (!c) return Promise.reject(new Error('Algolia client unavailable'));
    if (typeof c.multipleQueries === 'function') return c.multipleQueries(queries);   // v3 / v4
    if (typeof c.search !== 'function') return Promise.reject(new Error('Algolia client has no multi-search method'));
    if (typeof c.initIndex === 'function') return c.search(queries);                  // v4: array + nested params
    // v5: initIndex removed → object wrapper with search params flattened onto each request.
    var requests = queries.map(function(q) {
      var r = { indexName: q.indexName, query: q.query };
      if (q.params) { for (var k in q.params) { if (q.params.hasOwnProperty(k)) r[k] = q.params[k]; } }
      return r;
    });
    return c.search({ requests: requests });
  }

  // Chunk Algolia multi-queries into batches of ALGOLIA_QUERY_CHUNK to avoid timeouts
  // on large paste/import lookups. Sends chunks sequentially, preserves result order.
  var ALGOLIA_QUERY_CHUNK = 50;
  function _multiSearchChunked(queries) {
    if (!queries.length) return Promise.resolve({ results: [] });
    if (queries.length <= ALGOLIA_QUERY_CHUNK) return algoliaMultiSearch(queries);
    var chunks = [];
    for (var i = 0; i < queries.length; i += ALGOLIA_QUERY_CHUNK) {
      chunks.push(queries.slice(i, i + ALGOLIA_QUERY_CHUNK));
    }
    var all = [];
    return chunks.reduce(function(p, chunk) {
      return p.then(function() {
        return algoliaMultiSearch(chunk).then(function(resp) {
          (resp.results || []).forEach(function(r) { all.push(r); });
        });
      });
    }, Promise.resolve()).then(function() { return { results: all }; });
  }

  // Send all SKU lookups in one HTTP round-trip; second batch normalized fallback for misses.
  // Returns { [originalSku]: hits[] } — empty arrays for unmatched SKUs.
  function batchAlgoliaSearch(rawSkus) {
    // Failure contract: infrastructure failures (no client / no index / request error) resolve
    // to { __lookupFailed: true } so callers can distinguish "Algolia is down" from "no matches".
    // Backward compatible — callers that only index by SKU string (quick-order) see the same
    // empty-hits behavior as before and never touch the marker key.
    var c = _client();
    if (!c) {
      console.warn('[BCSProductLookup] Algolia searchClient not available');
      return Promise.resolve({ __lookupFailed: true });
    }
    if (!rawSkus.length) return Promise.resolve({});
    if (!_config.algoliaIndex) {
      console.error('[BCSProductLookup] algoliaIndex not configured');
      return Promise.resolve({ __lookupFailed: true });
    }

    var indexName = _config.algoliaIndex;
    function buildQuery(q) { return { indexName: indexName, query: q, params: { hitsPerPage: 5 } }; }
    function publishedOnly(hits) {
      return (hits || []).filter(function(h) { return h.published_at; });
    }

    var firstQueries = rawSkus.map(buildQuery);
    return _multiSearchChunked(firstQueries).then(function(response) {
      var hitsBySku = {};
      var fallbackPlan = [];
      (response.results || []).forEach(function(res, i) {
        var sku = rawSkus[i];
        var hits = publishedOnly(res.hits);
        hitsBySku[sku] = hits;
        if (!hits.length) {
          var norm = normalizeSku(sku);
          if (norm && norm !== sku.toLowerCase()) {
            fallbackPlan.push({ sku: sku, normalized: norm });
          }
        }
      });
      if (!fallbackPlan.length) return hitsBySku;

      var fallbackQueries = fallbackPlan.map(function(f) { return buildQuery(f.normalized); });
      return _multiSearchChunked(fallbackQueries).then(function(response2) {
        (response2.results || []).forEach(function(res, i) {
          hitsBySku[fallbackPlan[i].sku] = publishedOnly(res.hits);
        });
        return hitsBySku;
      });
    }).catch(function(e) {
      console.error('[BCSProductLookup] Batched Algolia lookup failed:', e);
      return { __lookupFailed: true };
    });
  }

  // Single-query search for live autocomplete. Returns published-only hits.
  function searchSingle(query, hitsPerPage) {
    var c = _client();
    if (!c) return Promise.reject(new Error('Algolia client unavailable'));
    if (!_config.algoliaIndex) return Promise.reject(new Error('algoliaIndex not configured'));
    var perPage = (typeof hitsPerPage === 'number' && hitsPerPage > 0) ? hitsPerPage : 8;
    var indexName = _config.algoliaIndex;
    function keepPublished(hits) { return (hits || []).filter(function(h) { return h.published_at; }); }
    // v3 / v4: index-scoped search via initIndex(). v5 removes initIndex — route through the multi-search
    // endpoint and read results[0] (the search-only "lite" client exposes search({requests}) but not
    // necessarily searchSingleIndex). Capability-detected so v4 behavior is unchanged.
    if (typeof c.initIndex === 'function') {
      return c.initIndex(indexName).search(query, { hitsPerPage: perPage }).then(function(d) {
        return keepPublished(d.hits);
      });
    }
    return c.search({ requests: [{ indexName: indexName, query: query, hitsPerPage: perPage }] }).then(function(resp) {
      var first = (resp && resp.results && resp.results[0]) || {};
      return keepPublished(first.hits);
    });
  }

  // ---------- Promise-cached fetches ----------

  // Slim per-product fetch: variants (B2B price + inventory) + discontinued + restricted-class-id.
  // Caches the in-flight Promise (NOT the resolved value) so concurrent callers for the same
  // handle dedupe to a single network request.
  function fetchBomMeta(handle) {
    if (!handle) return Promise.resolve(null);
    if (handle in _metaCache) return _metaCache[handle];
    var p = fetch('/products/' + handle + '?view=bom-meta', { credentials: 'same-origin' })
      .then(function(r) { return r.ok ? r.text() : null; })
      .then(function(t) {
        if (!t) return null;
        try { return JSON.parse(t.trim()); }
        catch (e) {
          console.warn('[BCSProductLookup] bom-meta parse failed for', handle, e);
          return null;
        }
      })
      .catch(function() { return null; })
      .then(function(result) {
        // Evict on failure: a null result (network/timeout/non-200/parse) must not be cached
        // for the page session, or "Retry product details" and re-lookups can never recover.
        if (result === null) delete _metaCache[handle];
        return result;
      });
    _metaCache[handle] = p;
    return p;
  }

  // Lazy alternatives fetch — heavier template, only called for matched-discontinued rows.
  function fetchBomAlts(handle) {
    if (!handle) return Promise.resolve(null);
    if (handle in _altsCache) return _altsCache[handle];
    var p = fetch('/products/' + handle + '?view=bom-alts', { credentials: 'same-origin' })
      .then(function(r) { return r.ok ? r.text() : null; })
      .then(function(t) {
        if (!t) return null;
        try { return JSON.parse(t.trim()); }
        catch (e) {
          console.warn('[BCSProductLookup] bom-alts parse failed for', handle, e);
          return null;
        }
      })
      .catch(function() { return null; })
      .then(function(result) {
        // Evict on failure — see fetchBomMeta; same retry-recovery rationale.
        if (result === null) delete _altsCache[handle];
        return result;
      });
    _altsCache[handle] = p;
    return p;
  }

  // ---------- Cart writes ----------

  var DEFAULT_CHUNK_SIZE = 50;

  // POST `items` to /cart/add.js in chunks of DEFAULT_CHUNK_SIZE, sequentially.
  // options.onProgress(succeededBatch, doneCount, totalBatches) — called after each successful chunk.
  // options.refreshBubble (default true) — refresh #cart-icon-bubble innerHTML.
  // options.openDrawer (default true) — dispatch 'dispatch:cart-drawer:refresh' event.
  // Returns a Promise that resolves on success or rejects with an Error on chunk failure.
  function addItemsToCart(items, options) {
    options = options || {};
    var refreshBubble = options.refreshBubble !== false;
    var openDrawer = options.openDrawer !== false;
    var onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
    var signal = options.signal || null;

    if (!items || !items.length) return Promise.resolve();

    var size = DEFAULT_CHUNK_SIZE;
    var batches = [];
    for (var i = 0; i < items.length; i += size) batches.push(items.slice(i, i + size));
    var totalBatches = batches.length;
    var done = 0;

    var chunkPromise = batches.reduce(function(p, batch) {
      return p.then(function() {
        if (signal && signal.aborted) throw new Error('Cart add aborted');
        // Strip any synthetic internal-only fields (e.g. _rowId) before sending.
        var payload = batch.map(function(it) {
          return { id: it.id, quantity: it.quantity, properties: it.properties || {} };
        });
        var fetchOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: payload })
        };
        if (signal) fetchOptions.signal = signal;
        return fetch('/cart/add.js', fetchOptions).then(function(res) {
          if (!res.ok) {
            return res.json().then(function(err) {
              throw new Error((err && err.description) || ('Batch ' + (done+1) + '/' + totalBatches + ' failed'));
            });
          }
          done++;
          if (onProgress) onProgress(batch, done, totalBatches);
        });
      });
    }, Promise.resolve());

    return chunkPromise.then(function() {
      if (refreshBubble) {
        return fetch('/?sections=cart-icon-bubble').then(function(r) { return r.json(); }).then(function(sections) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) bubble.innerHTML = sections['cart-icon-bubble'];
        }).catch(function(e) { console.warn('[BCSProductLookup] Cart icon update failed:', e); });
      }
    }).then(function() {
      if (openDrawer) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }
    });
  }

  // ---------- Public API ----------

  window.BCSProductLookup = {
    configure: configure,
    escape: escape,
    normalizeSku: normalizeSku,
    isRestricted: isRestricted,
    scoreHit: scoreHit,
    scoreToStatus: scoreToStatus,
    stockSpan: stockSpan,
    stockLine: stockLine,
    algoliaMultiSearch: algoliaMultiSearch,
    batchAlgoliaSearch: batchAlgoliaSearch,
    searchSingle: searchSingle,
    fetchBomMeta: fetchBomMeta,
    fetchBomAlts: fetchBomAlts,
    addItemsToCart: addItemsToCart
  };
})();
